import type {
  CommodityGroupKey,
  IApplicationLivestockRecord,
  ILivestockCatalogueItem,
  ILivestockFormState,
  ILivestockRecordSource,
  SellOffFrequency,
} from './productionInfo.types';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const COMMODITY_GROUP_BY_NAME: Record<string, CommodityGroupKey> = {
  livestock: 'livestock',
  fruits: 'fruits',
  vegetables: 'vegetables',
  grains: 'grains',
};

export const EMPTY_LIVESTOCK_FORM: ILivestockFormState = {
  livestockId: '',
  quantity: '',
  type: '',
  productGrading: '',
  price: '',
  notes: '',
};

export const PRODUCT_GRADING_OPTIONS = ['A', 'B', 'C'];

export const SELL_OFF_FREQUENCY_OPTIONS: Array<{
  value: SellOffFrequency;
  label: string;
}> = [
  {value: 'not-selling', label: 'Not selling'},
  {value: 'once-off', label: 'Once-off'},
  {value: 'custom', label: 'Custom monthly'},
];

export function lshToNumberOrZero(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function lshMonthKeyFromDate(year: number, month: number): string {
  return `${MONTH_LABELS[month]}_${year}`;
}

export function lshGenerateSellOffMonths(
  span: number,
): Array<{key: string; label: string}> {
  const current = new Date();
  const first = new Date(current.getFullYear(), current.getMonth() + 1, 1);

  return Array.from({length: span}, (_, index) => {
    const date = new Date(first.getFullYear(), first.getMonth() + index, 1);
    const key = lshMonthKeyFromDate(date.getFullYear(), date.getMonth());
    return {
      key,
      label: key.replace('_', ' '),
    };
  });
}

export function lshDescribeSellOffPeriod(value?: string | null): string {
  if (!value) return 'Not selling';

  try {
    const parsed = JSON.parse(value);
    const frequency = String(parsed?.frequency || 'not-selling');

    if (frequency === 'once-off') {
      return parsed?.month
        ? `Once-off (${String(parsed.month).replace('_', ' ')})`
        : 'Once-off';
    }

    if (frequency === 'custom') {
      const monthlyValues =
        parsed?.monthlyValues && typeof parsed.monthlyValues === 'object'
          ? Object.entries(parsed.monthlyValues).filter(
              ([, amount]) => Number(amount) > 0,
            )
          : [];

      return monthlyValues.length > 0
        ? `Custom (${monthlyValues.length} month(s))`
        : 'Custom';
    }

    return 'Not selling';
  } catch {
    return String(value).replace('_', ' ');
  }
}

export function lshFormatLivestockLabel(value?: string | null): string {
  return String(value || '')
    .trim()
    .replace(/_/g, ' ');
}

export function lshFormatEnumLabel(value?: string | null): string {
  return String(value || '')
    .trim()
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function lshNormalizeProductGrading(
  value?: string | null,
): 'A' | 'B' | 'C' | null {
  const normalized = String(value || '')
    .trim()
    .toUpperCase()
    .replace(/^GRADE\s+/, '');

  if (normalized === 'A' || normalized === 'B' || normalized === 'C') {
    return normalized;
  }

  return null;
}

export function lshMapLivestockCatalogueItem(
  item: ILivestockCatalogueItem | null | undefined,
): ILivestockCatalogueItem | null {
  const id = Number(item?.id);
  if (!Number.isFinite(id)) return null;

  return {
    id,
    name: String(item?.name || '').trim() || `Livestock ${id}`,
    livestockType: String(item?.livestockType || '').trim(),
    livestockProduct: String(item?.livestockProduct || '').trim(),
  };
}

export function lshMapLivestockRecord(
  record: ILivestockRecordSource,
  applicationId?: number,
): IApplicationLivestockRecord {
  const livestockId = Number(record?.livestock?.id);
  const recordId = Number(record?.id);

  return {
    id: Number.isFinite(recordId) ? recordId : undefined,
    quantity: Number(record?.quantity || 0),
    notes: String(record?.notes || '').trim() || null,
    type: String(record?.type || '').trim() || null,
    productGrading:
      String(record?.productGrading || '')
        .trim()
        .toUpperCase() || null,
    sellOfPeriod: String(record?.sellOfPeriod || '').trim() || null,
    price: Number(record?.price || 0),
    livestock: Number.isFinite(livestockId)
      ? {
          id: livestockId,
          name:
            String(record?.livestock?.name || '').trim() ||
            `Livestock ${livestockId}`,
          livestockType: String(record?.livestock?.livestockType || '').trim(),
          livestockProduct: String(
            record?.livestock?.livestockProduct || '',
          ).trim(),
        }
      : null,
    application: applicationId ? {id: applicationId} : undefined,
  };
}
